/* eslint-disable @typescript-eslint/no-unused-vars */
import './bootstrapConsole';
import { I18nextProvider } from 'react-i18next';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { UserProvider, CompanyProvider, BrandProvider } from 'services/appContext';
import ApiService from 'services/ApiService';
import Favicon from 'assets/img/orgcloud/favicon.png';
import i18n from './i18n';
import {
  buildManifestData,
  updateManifestLink,
  updateFaviconLink,
  ensureAppleTouchIcon,
} from 'services/pwaBranding';
import { hydrateActiveClientIdFromUrl } from 'utils/clientSwitchUrl';
import { getTenantRoutingIdentifier, isLocalDevelopmentHost, isMainDomain } from 'utils/domainUtils';
import { isInstalledAppRuntime } from 'utils/pwaRuntime';

const DEFAULT_BRAND_NAME = 'OrgCloud';
const PWA_UPDATE_RELOAD_KEY = 'orgcloud:pwa:sw-reload-pending';
const IS_PRODUCTION_BUILD = process.env.NODE_ENV === 'production';
const ENABLE_DEV_SERVICE_WORKER = process.env.REACT_APP_ENABLE_DEV_SERVICE_WORKER === 'true';
const isStandalonePwaRuntime = isInstalledAppRuntime;
const isIOSLikeRuntime = () => {
  if (typeof window === 'undefined') return false;
  const userAgent = window.navigator.userAgent || '';
  const platform = window.navigator.platform || '';
  return /iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
};
const canRegisterServiceWorker = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  !isLocalDevelopmentHost(window.location.hostname) &&
  (IS_PRODUCTION_BUILD ||
    (
      ENABLE_DEV_SERVICE_WORKER &&
      window.isSecureContext === true
    ));

hydrateActiveClientIdFromUrl();
if (typeof window !== 'undefined') {
  const startupParams = new URLSearchParams(window.location.search);
  if (startupParams.has('reset')) {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      console.warn('No se pudo limpiar almacenamiento local durante reset PWA', error);
    }
  }

  sessionStorage.removeItem(PWA_UPDATE_RELOAD_KEY);
  document.documentElement.classList.toggle('orgcloud-pwa-standalone', isStandalonePwaRuntime());
  document.documentElement.classList.toggle('orgcloud-ios-pwa', isStandalonePwaRuntime() && isIOSLikeRuntime());

  const updateOrgCloudViewportHeight = () => {
    if (isStandalonePwaRuntime() && isIOSLikeRuntime()) {
      document.documentElement.style.setProperty(
        '--orgcloud-app-height',
        'calc(100dvh + env(safe-area-inset-bottom, 0px))'
      );
      document.documentElement.style.setProperty('--orgcloud-visual-offset-top', '0px');
      document.documentElement.style.setProperty('--orgcloud-visual-bottom-gap', '0px');
      return;
    }

    const visualViewport = window.visualViewport;
    const visualHeight = visualViewport?.height || 0;
    const visualOffsetTop = visualViewport?.offsetTop || 0;
    const innerViewportHeight = Math.round(window.innerHeight || 0);
    const measuredViewportHeight = Math.round((visualHeight || innerViewportHeight || 0) + visualOffsetTop);
    const viewportHeight = Math.max(measuredViewportHeight, innerViewportHeight);
    const bottomGap = Math.max(
      0,
      innerViewportHeight - Math.round((visualHeight || innerViewportHeight) + visualOffsetTop)
    );
    if (viewportHeight > 0) {
      document.documentElement.style.setProperty('--orgcloud-app-height', `${viewportHeight}px`);
      document.documentElement.style.setProperty('--orgcloud-visual-offset-top', `${Math.round(visualOffsetTop)}px`);
      document.documentElement.style.setProperty('--orgcloud-visual-bottom-gap', `${bottomGap}px`);
    }
  };

  updateOrgCloudViewportHeight();
  window.visualViewport?.addEventListener('resize', updateOrgCloudViewportHeight);
  window.visualViewport?.addEventListener('scroll', updateOrgCloudViewportHeight);
  window.addEventListener('resize', updateOrgCloudViewportHeight);
  window.addEventListener('orientationchange', updateOrgCloudViewportHeight);
}

const normalizeBrandName = (value?: string | null) =>
  typeof value === 'string' ? value.trim() : '';

const setMetaTag = (name: string, content: string) => {
  if (typeof document === 'undefined') return;
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
};

const applyBrandAssets = (brandName: string, faviconUrl: string, manifestPayload?: unknown, brandingVersion?: string) => {
  const safeBrandName = normalizeBrandName(brandName) || DEFAULT_BRAND_NAME;
  const manifestData = buildManifestData(manifestPayload, safeBrandName, faviconUrl, brandingVersion);
  const themeColor = manifestData?.theme_color || '#08111f';
  updateFaviconLink(faviconUrl, brandingVersion);
  updateManifestLink(manifestData);
  ensureAppleTouchIcon(faviconUrl, brandingVersion);
  if (typeof document !== 'undefined') {
    document.title = safeBrandName;
  }
  setMetaTag('theme-color', themeColor);
  setMetaTag('mobile-web-app-capable', 'yes');
  setMetaTag('apple-mobile-web-app-capable', 'yes');
  setMetaTag('apple-mobile-web-app-title', safeBrandName);
  setMetaTag('apple-mobile-web-app-status-bar-style', 'black-translucent');
  setMetaTag('application-name', safeBrandName);
};

const updateFaviconAndPWA = async () => {
  const identifier = getTenantRoutingIdentifier();
  if (!identifier || isMainDomain()) {
    applyBrandAssets(DEFAULT_BRAND_NAME, Favicon);
    return;
  }

  if (typeof document !== 'undefined') {
    document.title = '';
  }

  try {
    const data = await ApiService.readBranding(identifier, false, false);
    const uniqueName = data?.uniqueName || data?.name || DEFAULT_BRAND_NAME;
    const brandName = uniqueName.charAt(0).toUpperCase() + uniqueName.slice(1).toLowerCase();
    const faviconUrl = data?.img || '';
    applyBrandAssets(brandName, faviconUrl, data?.manifest, data?.brandingVersion);
  } catch (error) {
    console.error('Error actualizando favicon y manifest:', error);
    updateFaviconLink('');
    ensureAppleTouchIcon('');
  }
};

updateFaviconAndPWA();

const clearDevelopmentServiceWorkerState = async () => {
  if (typeof window === 'undefined') return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch (error) {
    console.warn('No se pudieron desregistrar service workers en desarrollo', error);
  }

  try {
    if ('caches' in window) {
      const cacheNames = await window.caches.keys();
      await Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName)));
    }
  } catch (error) {
    console.warn('No se pudieron limpiar caches PWA en desarrollo', error);
  }
};

if (canRegisterServiceWorker()) {
  navigator.serviceWorker.register('/service-worker.js').then((registration) => {
    const triggerSoftReload = () => {
      if (sessionStorage.getItem(PWA_UPDATE_RELOAD_KEY) === '1') return;
      sessionStorage.setItem(PWA_UPDATE_RELOAD_KEY, '1');
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener('controllerchange', triggerSoftReload);

    const watchRegistration = (reg: ServiceWorkerRegistration) => {
      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            installing.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      });
    };

    watchRegistration(registration);
    registration.update().catch(() => undefined);
    window.setInterval(() => {
      registration.update().catch(() => undefined);
    }, 5 * 60 * 1000);
  }).catch((error) => {
    console.warn('No se pudo registrar el service worker de OrgCloud', error);
  });
} else if (
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator
) {
  clearDevelopmentServiceWorkerState();
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <I18nextProvider i18n={i18n}>
    <BrandProvider>
      <UserProvider>
        <CompanyProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </CompanyProvider>
      </UserProvider>
    </BrandProvider>
  </I18nextProvider>
);

if (typeof window !== 'undefined') {
  const hideBootSplash = (window as any).__ORGCLOUD_HIDE_BOOT_SPLASH__ || (() => {
    const bootSplash = document.getElementById('boot-splash');
    if (!bootSplash) return;
    bootSplash.classList.add('hidden');
    window.setTimeout(() => {
      bootSplash.remove();
      (window as any).__ORGCLOUD_BOOT_SPLASH_ACTIVE__ = false;
    }, 220);
  });

  (window as any).__ORGCLOUD_HIDE_BOOT_SPLASH__ = hideBootSplash;

  // App hides this only after React has committed its route/loading state.
}
